import { useState, useEffect } from 'react';
import styles from './UnifiedAuthModal.module.scss';
import EZIOKService, { EZIOKResult } from '../../libs/EZIOKService';
import MobileOKService from '../../libs/MobileOKService';

// 인증사업자 로고 이미지 import
import kakaoLogo from '../../assets/authLogoImages/100_kakaotalk.png';
import naverLogo from '../../assets/authLogoImages/100_naver.png';
import passLogo from '../../assets/authLogoImages/100_pass.png';
import tossLogo from '../../assets/authLogoImages/100_toss.png';
import shinhanLogo from '../../assets/authLogoImages/100_ShinhanSIGN.png';
import kbstarLogo from '../../assets/authLogoImages/100_KBstar.png';
import hanaLogo from '../../assets/authLogoImages/100_hana.png';
import wooriLogo from '../../assets/authLogoImages/100_WooRi_WON.png';
import nhLogo from '../../assets/authLogoImages/100_NH_NEW.png';
import kakaobankLogo from '../../assets/authLogoImages/100_kakaobank.png';
import banksaladLogo from '../../assets/authLogoImages/100_banksalad.png';
import samsungpassLogo from '../../assets/authLogoImages/100_SamsungPass.png';
import yeskeyLogo from '../../assets/authLogoImages/logo_yeskey_1.png';

interface AuthProvider {
  code: string;
  name: string;
  type: string;
  logo: string | null;
  ciStorable: boolean;
  authMethod: 'easy' | 'mobile';
}

// 전체 인증 제공사 목록 (간편인증 + 휴대폰인증)
const AUTH_PROVIDERS: AuthProvider[] = [
  { code: 'kakao', name: '카카오', type: '민간', logo: kakaoLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'naver', name: '네이버', type: '민간', logo: naverLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'passauth', name: 'PASS', type: '통신사', logo: passLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'toss', name: '토스', type: '금융', logo: tossLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'shinhan', name: '신한은행', type: '금융', logo: shinhanLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'kbstar', name: 'KB국민은행', type: '금융', logo: kbstarLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'kebhana', name: '하나은행', type: '금융', logo: hanaLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'woori', name: '우리은행', type: '금융', logo: wooriLogo, ciStorable: false, authMethod: 'easy' },
  { code: 'nhlife', name: 'NH농협', type: '금융', logo: nhLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'kakaobank', name: '카카오뱅크', type: '금융', logo: kakaobankLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'banksalad', name: '뱅크샐러드', type: '금융', logo: banksaladLogo, ciStorable: true, authMethod: 'easy' },
  { code: 'signgate', name: '삼성패스', type: '공동인증', logo: samsungpassLogo, ciStorable: false, authMethod: 'easy' },
  { code: 'yeskey', name: '금융결제원', type: '공동인증', logo: yeskeyLogo, ciStorable: false, authMethod: 'easy' },
  // 휴대폰 인증 (마지막에 배치)
  { code: 'mobile', name: '휴대폰인증', type: 'SMS', logo: null, ciStorable: true, authMethod: 'mobile' },
];

interface UnifiedAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: EZIOKResult) => void;
  onError?: (error: Error) => void;
  userId?: number;
  forRegistration?: boolean;
  // 비즈니스 파라미터 (주문/결제 관련)
  pcode?: number;           // 상품 ID
  cartString?: string;      // 장바구니 문자열
  isDirect?: string;        // 직접구매 여부 (Y/N)
  purpose?: 'order' | 'signup' | 'adult' | 'general';  // 인증 용도
}

const UnifiedAuthModal: React.FC<UnifiedAuthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  userId,
  forRegistration = false,
  pcode,
  cartString,
  isDirect,
  purpose = 'general',
}) => {
  const [selectedProvider, setSelectedProvider] = useState<AuthProvider | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSdkReady, setIsSdkReady] = useState(false);

  // 모달 열릴 때 SDK 미리 로드
  useEffect(() => {
    if (isOpen) {
      const loadSDKs = async () => {
        try {
          await Promise.all([
            MobileOKService.loadScript(),
            EZIOKService.loadScript(),
          ]);
          setIsSdkReady(true);
          console.log('[UnifiedAuthModal] SDKs loaded successfully');
        } catch (err) {
          console.error('[UnifiedAuthModal] SDK load failed:', err);
        }
      };
      loadSDKs();
    }
  }, [isOpen]);

  // postMessage 이벤트 리스너 (백엔드 /callback에서 HTML 응답으로 postMessage 전송)
  // 기존 NICE 인증과 동일한 방식
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('[UnifiedAuthModal] postMessage received:', event.data, 'from:', event.origin);

      const data = event.data;
      if (typeof data !== 'object' || data === null) {
        return;
      }

      // 기존 NICE 인증 형식: { success: "true" | "false", message: "..." }
      if (data.success === 'true') {
        console.log('[UnifiedAuthModal] 본인인증 성공');
        setIsLoading(false);
        onSuccess({
          success: true,
          message: '인증 성공',
          data: data.data || {},
        });
        onClose();
      } else if (data.success === 'false') {
        console.log('[UnifiedAuthModal] 본인인증 실패:', data.message);
        setIsLoading(false);
        onError?.(new Error(data.message || '본인인증에 실패했습니다.'));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSuccess, onError, onClose]);

  // SDK 콜백 등록 - useMobileOK 훅과 동일한 방식
  useEffect(() => {
    // Mobile-OK 콜백: useMobileOK와 동일하게 직접 onSuccess 호출
    (window as any).mobileOKCallback = (response: string) => {
      console.log('[UnifiedAuthModal] mobileOKCallback called:', response);
      setIsLoading(false);

      try {
        const data = JSON.parse(response);
        console.log('[UnifiedAuthModal] Parsed MobileOK data:', data);

        // 성공 코드: 0, 0000, 2000 (useMobileOK와 동일)
        const successCodes = ['0', '0000', '2000'];
        if (successCodes.includes(String(data.resultCode))) {
          console.log('[UnifiedAuthModal] MobileOK auth success');
          onSuccess({
            success: true,
            message: data.resultMsg || '인증 성공',
            data: data.data || data,
          });
          onClose();
        } else {
          onError?.(new Error(data.resultMsg || '본인인증에 실패했습니다.'));
        }
      } catch (err) {
        console.error('[UnifiedAuthModal] mobileOKCallback parse error:', err);
        onError?.(new Error('본인인증 처리 중 오류가 발생했습니다.'));
      }
    };

    // EZ-IOK 콜백
    (window as any).eziokCallback = (response: string) => {
      console.log('[UnifiedAuthModal] eziokCallback called:', response);
      setIsLoading(false);

      try {
        // JSON 형식 처리 (백엔드에서 JSON 응답 시)
        if (response.startsWith('{')) {
          const data = JSON.parse(response);
          console.log('[UnifiedAuthModal] Parsed EZIOK JSON data:', data);

          // 성공 코드: 0, 0000, 2000
          const successCodes = ['0', '0000', '2000'];
          if (successCodes.includes(String(data.resultCode))) {
            console.log('[UnifiedAuthModal] EZIOK auth success');
            onSuccess({
              success: true,
              message: data.resultMsg || '인증 성공',
              data: data.data || {},
            });
            onClose();
          } else {
            onError?.(new Error(data.resultMsg || '간편인증에 실패했습니다.'));
          }
        } else {
          // 파이프 구분자 형식 처리 (기존 SDK 형식: resultCode|keyToken|errorMsg)
          const parts = response.split('|');
          const resultCode = parts[0];
          const resultMsg = parts[2] || '';

          if (resultCode === '0') {
            console.log('[UnifiedAuthModal] EZIOK auth started successfully (pipe format)');
            // 파이프 형식은 중간 단계이므로 대기
          } else {
            onError?.(new Error(resultMsg || '간편인증에 실패했습니다.'));
          }
        }
      } catch (err) {
        console.error('[UnifiedAuthModal] eziokCallback parse error:', err);
        onError?.(new Error('간편인증 처리 중 오류가 발생했습니다.'));
      }
    };

    return () => {
      delete (window as any).mobileOKCallback;
      delete (window as any).eziokCallback;
    };
  }, [onSuccess, onError, onClose]);

  // 모달 열릴 때 선택 초기화
  useEffect(() => {
    if (isOpen) {
      setSelectedProvider(null);
    }
  }, [isOpen]);

  // 회원가입용이면 CI 저장 가능한 제공사만 필터링
  const availableProviders = forRegistration
    ? AUTH_PROVIDERS.filter((p) => p.ciStorable)
    : AUTH_PROVIDERS;

  // 인증하기 버튼 클릭
  const handleAuth = async () => {
    if (!selectedProvider) return;

    setIsLoading(true);

    try {
      if (selectedProvider.authMethod === 'mobile') {
        // 휴대폰 인증 (Mobile-OK) - MOKToken 방식
        // startAuth가 txId 생성 및 sessionStorage 저장 처리
        await MobileOKService.startAuth({
          usageCode: '01005',
          userId,
        });
      } else {
        // 간편인증 (EZ-IOK) - keytoken 방식
        await EZIOKService.startAuth({
          userId,
          authProviderCode: selectedProvider.code,
        });
      }
      // 팝업이 열리면 로딩 해제 (사용자가 팝업을 닫아도 버튼이 정상 상태로 복귀)
      setIsLoading(false);
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error('인증 요청 실패'));
      setIsLoading(false);
    }
  };

  // 인증하기 버튼 활성화 조건
  const isAuthButtonEnabled = () => {
    if (isLoading || !selectedProvider || !isSdkReady) return false;
    return true;
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className={styles.header}>
          <h2 className={styles.title}>본인인증</h2>
          <button className={styles.closeButton} onClick={onClose}>
            &times;
          </button>
        </div>

        {/* 콘텐츠 */}
        <div className={styles.content}>
          {/* 진료용 안내 가이드 */}
          {purpose === 'order' && (
            <div className={styles.guideSection}>
              <p className={styles.guideText}>
                비대면진료 시범사업 개정('24.12.2.)으로 본인 인증이 필요합니다.
              </p>
              <p className={styles.guideText}>
                인증은 <span>6개월</span>에 한번 진행되며, 본인 명의로 인증해 주세요.
              </p>
              <p className={styles.guideText}>
                인증이 어려우시면 하단 물음표 버튼으로 고객센터에 문의해 주세요.
              </p>
            </div>
          )}
          <div className={styles.providerGrid}>
            {availableProviders.map((provider) => (
              <button
                key={provider.code}
                className={`${styles.providerItem} ${
                  selectedProvider?.code === provider.code ? styles.selected : ''
                }`}
                onClick={() => setSelectedProvider(provider)}
              >
                {provider.logo ? (
                  <img
                    src={provider.logo}
                    alt={provider.name}
                    className={styles.providerLogo}
                  />
                ) : (
                  <div className={styles.mobileIconSmall}>📱</div>
                )}
                <span className={styles.providerName}>{provider.name}</span>
              </button>
            ))}
          </div>
          {forRegistration && (
            <p className={styles.notice}>
              * 회원가입에는 CI 저장이 가능한 인증수단만 사용 가능합니다.
            </p>
          )}
        </div>

        {/* 인증하기 버튼 */}
        <div className={styles.footer}>
          <button
            className={styles.authButton}
            onClick={handleAuth}
            disabled={!isAuthButtonEnabled()}
          >
            {!isSdkReady ? '로딩 중...' : isLoading ? '인증 중...' : '인증하기'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default UnifiedAuthModal;
